/**
 * Approval Process Storage Service
 *
 * Manages approval processes and requests in localStorage.
 * Provides full CRUD operations for processes and request lifecycle management.
 *
 * Phase 1: localStorage (mock)
 * Phase 2: Real API calls
 *
 * @module services/approvalProcessStorage
 */

import {
  ApprovalProcess,
  ApprovalRequest,
  ApprovalStepHistory,
  PendingApproval,
  ApprovalStats,
  ProcessStats,
  ApprovalAction,
  CreateApprovalProcessPayload,
  UpdateApprovalProcessPayload,
  SubmitForApprovalPayload,
  ApprovalActionPayload
} from '../types/approvalProcesses';

// Storage keys
const STORAGE_KEY_PROCESSES = 'crm_approval_processes';
const STORAGE_KEY_REQUESTS = 'crm_approval_requests';

/**
 * Generate unique ID
 */
const generateId = (prefix: string): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get current timestamp
 */
const now = (): string => new Date().toISOString();

/**
 * Calculate days between two dates
 */
const daysBetween = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calculate hours between two dates
 */
const hoursBetween = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.round(diffTime / (1000 * 60 * 60));
};

/**
 * Approval Process Storage Service
 */
class ApprovalProcessStorage {
  // ============================================
  // PROCESS CRUD OPERATIONS
  // ============================================

  /**
   * Get all approval processes
   */
  getAllProcesses(): ApprovalProcess[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY_PROCESSES);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading approval processes:', error);
      return [];
    }
  }

  /**
   * Get a single process by ID
   */
  getProcess(processId: string): ApprovalProcess | null {
    const processes = this.getAllProcesses();
    return processes.find(p => p.id === processId) || null;
  }

  /**
   * Get processes for a specific object type
   */
  getProcessesForObject(objectType: string): ApprovalProcess[] {
    const processes = this.getAllProcesses();
    return processes
      .filter(p => p.objectType === objectType && p.isActive)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Create a new approval process
   */
  createProcess(payload: CreateApprovalProcessPayload): ApprovalProcess {
    const processes = this.getAllProcesses();

    const newProcess: ApprovalProcess = {
      ...payload,
      id: generateId('ap'),
      steps: payload.steps.map((step, index) => ({
        ...step,
        id: generateId('step'),
        stepNumber: index + 1
      })),
      createdDate: now(),
      createdBy: 'current_user', // TODO: Get from auth context
      lastModifiedDate: now(),
      lastModifiedBy: 'current_user'
    };

    processes.push(newProcess);
    this.saveProcesses(processes);

    return newProcess;
  }

  /**
   * Update an existing process
   */
  updateProcess(payload: UpdateApprovalProcessPayload): ApprovalProcess | null {
    const processes = this.getAllProcesses();
    const index = processes.findIndex(p => p.id === payload.id);

    if (index === -1) return null;

    const updated: ApprovalProcess = {
      ...processes[index],
      ...payload,
      lastModifiedDate: now(),
      lastModifiedBy: 'current_user'
    };

    // If steps were updated, ensure IDs and step numbers
    if (payload.steps) {
      updated.steps = payload.steps.map((step, idx) => ({
        ...step,
        id: step.id || generateId('step'),
        stepNumber: idx + 1
      }));
    }

    processes[index] = updated;
    this.saveProcesses(processes);

    return updated;
  }

  /**
   * Delete a process
   */
  deleteProcess(processId: string): boolean {
    const processes = this.getAllProcesses();
    const filtered = processes.filter(p => p.id !== processId);

    if (filtered.length === processes.length) return false;

    this.saveProcesses(filtered);
    return true;
  }

  /**
   * Toggle process active status
   */
  toggleProcessStatus(processId: string): ApprovalProcess | null {
    const processes = this.getAllProcesses();
    const index = processes.findIndex(p => p.id === processId);

    if (index === -1) return null;

    processes[index] = {
      ...processes[index],
      isActive: !processes[index].isActive,
      lastModifiedDate: now(),
      lastModifiedBy: 'current_user'
    };

    this.saveProcesses(processes);
    return processes[index];
  }

  /**
   * Clone a process
   */
  cloneProcess(processId: string, newName: string): ApprovalProcess | null {
    const original = this.getProcess(processId);
    if (!original) return null;

    const cloned: ApprovalProcess = {
      ...original,
      id: generateId('ap'),
      name: newName,
      isActive: false,
      steps: original.steps.map(step => ({
        ...step,
        id: generateId('step')
      })),
      createdDate: now(),
      createdBy: 'current_user',
      lastModifiedDate: now(),
      lastModifiedBy: 'current_user'
    };

    const processes = this.getAllProcesses();
    processes.push(cloned);
    this.saveProcesses(processes);

    return cloned;
  }

  // ============================================
  // REQUEST CRUD OPERATIONS
  // ============================================

  /**
   * Get all approval requests
   */
  getAllRequests(): ApprovalRequest[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY_REQUESTS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading approval requests:', error);
      return [];
    }
  }

  /**
   * Get a single request by ID
   */
  getRequest(requestId: string): ApprovalRequest | null {
    const requests = this.getAllRequests();
    return requests.find(r => r.id === requestId) || null;
  }

  /**
   * Get requests for a specific record
   */
  getRequestsForRecord(recordId: string): ApprovalRequest[] {
    const requests = this.getAllRequests();
    return requests
      .filter(r => r.recordId === recordId)
      .sort((a, b) => new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime());
  }

  /**
   * Submit a record for approval
   */
  submitForApproval(payload: SubmitForApprovalPayload, submittedBy: string, submittedByName: string): ApprovalRequest | null {
    const process = this.getProcess(payload.processId);
    if (!process || !process.isActive) return null;

    const firstStep = process.steps[0];
    if (!firstStep) return null;

    const requestId = generateId('ar');
    const stepHistoryId = generateId('sh');

    // Create initial step history
    const initialStepHistory: ApprovalStepHistory = {
      id: stepHistoryId,
      stepNumber: 1,
      stepName: firstStep.name,
      assignedTo: this.resolveApprover(firstStep, payload.recordId),
      assignedToName: this.resolveApproverName(firstStep, payload.recordId),
      assignedDate: now(),
      status: 'pending',
      dueDate: firstStep.autoEscalateAfterDays
        ? new Date(Date.now() + firstStep.autoEscalateAfterDays * 24 * 60 * 60 * 1000).toISOString()
        : undefined
    };

    const newRequest: ApprovalRequest = {
      id: requestId,
      processId: payload.processId,
      processName: process.name,
      recordId: payload.recordId,
      recordName: payload.recordName,
      objectType: payload.objectType,
      status: 'pending',
      currentStepNumber: 1,
      totalSteps: process.steps.length,
      submittedBy,
      submittedByName,
      submittedDate: now(),
      comments: payload.comments,
      stepHistory: [initialStepHistory]
    };

    const requests = this.getAllRequests();
    requests.push(newRequest);
    this.saveRequests(requests);

    return newRequest;
  }

  // ============================================
  // APPROVAL ACTIONS
  // ============================================

  /**
   * Process an approval action
   */
  processAction(payload: ApprovalActionPayload, userId: string, userName: string): ApprovalRequest | null {
    const request = this.getRequest(payload.requestId);
    if (!request || request.status !== 'pending') return null;

    switch (payload.action) {
      case 'approve':
        return this.approveStep(request, userId, userName, payload.comments);
      case 'reject':
        return this.rejectStep(request, userId, userName, payload.comments);
      case 'reassign':
        if (!payload.reassignToUserId) return null;
        return this.reassignStep(request, payload.reassignToUserId, userId, userName, payload.comments);
      case 'recall':
        return this.recallRequest(request, userId, userName, payload.comments);
      case 'add_comment':
        return this.addComment(request, userId, userName, payload.comments);
      default:
        return null;
    }
  }

  /**
   * Approve current step
   */
  private approveStep(
    request: ApprovalRequest,
    userId: string,
    userName: string,
    comments?: string
  ): ApprovalRequest {
    const process = this.getProcess(request.processId);
    const currentHistory = request.stepHistory.find(
      h => h.stepNumber === request.currentStepNumber && h.status === 'pending'
    );

    if (currentHistory) {
      currentHistory.action = 'approve';
      currentHistory.actionDate = now();
      currentHistory.actionBy = userId;
      currentHistory.actionByName = userName;
      currentHistory.comments = comments;
      currentHistory.status = 'completed';
    }

    // Check if there are more steps
    const nextStepNumber = request.currentStepNumber + 1;
    const nextStep = process?.steps.find(s => s.stepNumber === nextStepNumber);

    if (nextStep) {
      // Move to next step
      request.currentStepNumber = nextStepNumber;

      const newStepHistory: ApprovalStepHistory = {
        id: generateId('sh'),
        stepNumber: nextStepNumber,
        stepName: nextStep.name,
        assignedTo: this.resolveApprover(nextStep, request.recordId),
        assignedToName: this.resolveApproverName(nextStep, request.recordId),
        assignedDate: now(),
        status: 'pending',
        dueDate: nextStep.autoEscalateAfterDays
          ? new Date(Date.now() + nextStep.autoEscalateAfterDays * 24 * 60 * 60 * 1000).toISOString()
          : undefined
      };

      request.stepHistory.push(newStepHistory);
    } else {
      // Final approval
      request.status = 'approved';
      request.completedDate = now();
      request.finalComments = comments;
    }

    return this.saveRequest(request);
  }

  /**
   * Reject current step
   */
  private rejectStep(
    request: ApprovalRequest,
    userId: string,
    userName: string,
    comments?: string
  ): ApprovalRequest {
    const currentHistory = request.stepHistory.find(
      h => h.stepNumber === request.currentStepNumber && h.status === 'pending'
    );

    if (currentHistory) {
      currentHistory.action = 'reject';
      currentHistory.actionDate = now();
      currentHistory.actionBy = userId;
      currentHistory.actionByName = userName;
      currentHistory.comments = comments;
      currentHistory.status = 'completed';
    }

    request.status = 'rejected';
    request.completedDate = now();
    request.finalComments = comments;

    return this.saveRequest(request);
  }

  /**
   * Reassign current step
   */
  private reassignStep(
    request: ApprovalRequest,
    newAssigneeId: string,
    userId: string,
    userName: string,
    comments?: string
  ): ApprovalRequest {
    const currentHistory = request.stepHistory.find(
      h => h.stepNumber === request.currentStepNumber && h.status === 'pending'
    );

    if (currentHistory) {
      currentHistory.action = 'reassign';
      currentHistory.actionDate = now();
      currentHistory.actionBy = userId;
      currentHistory.actionByName = userName;
      currentHistory.comments = comments;
      currentHistory.status = 'reassigned';
    }

    // Create new history entry for reassigned user
    const process = this.getProcess(request.processId);
    const currentStep = process?.steps.find(s => s.stepNumber === request.currentStepNumber);

    const newStepHistory: ApprovalStepHistory = {
      id: generateId('sh'),
      stepNumber: request.currentStepNumber,
      stepName: currentStep?.name || `Step ${request.currentStepNumber}`,
      assignedTo: newAssigneeId,
      assignedToName: `User ${newAssigneeId}`, // TODO: Look up actual name
      assignedDate: now(),
      status: 'pending',
      dueDate: currentStep?.autoEscalateAfterDays
        ? new Date(Date.now() + currentStep.autoEscalateAfterDays * 24 * 60 * 60 * 1000).toISOString()
        : undefined
    };

    request.stepHistory.push(newStepHistory);

    return this.saveRequest(request);
  }

  /**
   * Recall a request
   */
  private recallRequest(
    request: ApprovalRequest,
    userId: string,
    userName: string,
    comments?: string
  ): ApprovalRequest {
    const process = this.getProcess(request.processId);
    if (!process?.allowRecall) return request;

    // Only submitter can recall
    if (request.submittedBy !== userId) return request;

    const currentHistory = request.stepHistory.find(
      h => h.stepNumber === request.currentStepNumber && h.status === 'pending'
    );

    if (currentHistory) {
      currentHistory.action = 'recall';
      currentHistory.actionDate = now();
      currentHistory.actionBy = userId;
      currentHistory.actionByName = userName;
      currentHistory.comments = comments;
      currentHistory.status = 'completed';
    }

    request.status = 'recalled';
    request.completedDate = now();
    request.finalComments = comments;

    return this.saveRequest(request);
  }

  /**
   * Add comment to current step
   */
  private addComment(
    request: ApprovalRequest,
    userId: string,
    userName: string,
    comments?: string
  ): ApprovalRequest {
    const currentHistory = request.stepHistory.find(
      h => h.stepNumber === request.currentStepNumber && h.status === 'pending'
    );

    if (currentHistory && comments) {
      currentHistory.comments = currentHistory.comments
        ? `${currentHistory.comments}\n\n[${userName}]: ${comments}`
        : `[${userName}]: ${comments}`;
    }

    return this.saveRequest(request);
  }

  // ============================================
  // QUERIES
  // ============================================

  /**
   * Get pending approvals for a user
   */
  getPendingApprovalsForUser(userId: string): PendingApproval[] {
    const requests = this.getAllRequests().filter(r => r.status === 'pending');
    const pending: PendingApproval[] = [];

    for (const request of requests) {
      const currentHistory = request.stepHistory.find(
        h => h.stepNumber === request.currentStepNumber && h.status === 'pending'
      );

      if (currentHistory && currentHistory.assignedTo === userId) {
        const isOverdue = currentHistory.dueDate
          ? new Date(currentHistory.dueDate) < new Date()
          : false;

        pending.push({
          id: currentHistory.id,
          requestId: request.id,
          processName: request.processName,
          recordId: request.recordId,
          recordName: request.recordName,
          objectType: request.objectType,
          submittedBy: request.submittedBy,
          submittedByName: request.submittedByName,
          submittedDate: request.submittedDate,
          currentStep: currentHistory.stepName,
          currentStepNumber: request.currentStepNumber,
          totalSteps: request.totalSteps,
          dueDate: currentHistory.dueDate,
          isOverdue,
          daysWaiting: daysBetween(request.submittedDate, now())
        });
      }
    }

    return pending.sort((a, b) => {
      // Overdue first, then by days waiting
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      return b.daysWaiting - a.daysWaiting;
    });
  }

  /**
   * Get approval history for a record
   */
  getApprovalHistory(recordId: string): ApprovalRequest[] {
    return this.getRequestsForRecord(recordId);
  }

  /**
   * Get approval statistics
   */
  getStats(): ApprovalStats {
    const processes = this.getAllProcesses();
    const requests = this.getAllRequests();

    const today = new Date().toISOString().split('T')[0];

    const completedRequests = requests.filter(r =>
      r.completedDate && (r.status === 'approved' || r.status === 'rejected')
    );

    const avgTime = completedRequests.length > 0
      ? completedRequests.reduce((sum, r) =>
          sum + hoursBetween(r.submittedDate, r.completedDate!), 0
        ) / completedRequests.length
      : 0;

    const pendingRequests = requests.filter(r => r.status === 'pending');
    const overdueRequests = pendingRequests.filter(r => {
      const currentHistory = r.stepHistory.find(
        h => h.stepNumber === r.currentStepNumber && h.status === 'pending'
      );
      return currentHistory?.dueDate && new Date(currentHistory.dueDate) < new Date();
    });

    return {
      totalProcesses: processes.length,
      activeProcesses: processes.filter(p => p.isActive).length,
      pendingRequests: pendingRequests.length,
      approvedToday: requests.filter(r =>
        r.status === 'approved' && r.completedDate?.startsWith(today)
      ).length,
      rejectedToday: requests.filter(r =>
        r.status === 'rejected' && r.completedDate?.startsWith(today)
      ).length,
      avgApprovalTime: Math.round(avgTime * 10) / 10,
      overdueRequests: overdueRequests.length
    };
  }

  /**
   * Get statistics for a specific process
   */
  getProcessStats(processId: string): ProcessStats | null {
    const process = this.getProcess(processId);
    if (!process) return null;

    const requests = this.getAllRequests().filter(r => r.processId === processId);

    const completedRequests = requests.filter(r =>
      r.completedDate && (r.status === 'approved' || r.status === 'rejected')
    );

    const approvedCount = requests.filter(r => r.status === 'approved').length;
    const totalCompleted = completedRequests.length;

    const avgTime = completedRequests.length > 0
      ? completedRequests.reduce((sum, r) =>
          sum + hoursBetween(r.submittedDate, r.completedDate!), 0
        ) / completedRequests.length
      : 0;

    return {
      processId: process.id,
      processName: process.name,
      totalSubmitted: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      approved: approvedCount,
      rejected: requests.filter(r => r.status === 'rejected').length,
      recalled: requests.filter(r => r.status === 'recalled').length,
      approvalRate: totalCompleted > 0 ? Math.round((approvedCount / totalCompleted) * 100) : 0,
      avgCompletionTime: Math.round(avgTime * 10) / 10
    };
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Resolve approver ID based on step configuration
   */
  private resolveApprover(step: import('../types/approvalProcesses').ApprovalStep, _recordId: string): string {
    switch (step.approverType) {
      case 'user':
        return step.approverId || 'unknown';
      case 'role':
        return step.approverId || 'unknown';
      case 'manager':
        // TODO: Look up record owner's manager
        return 'manager_user';
      case 'queue':
        return step.approverId || 'unknown';
      case 'related_user':
        // TODO: Look up field value from record
        return 'related_user';
      default:
        return 'unknown';
    }
  }

  /**
   * Resolve approver name based on step configuration
   */
  private resolveApproverName(step: import('../types/approvalProcesses').ApprovalStep, _recordId: string): string {
    switch (step.approverType) {
      case 'user':
        return `User (${step.approverId})`;
      case 'role':
        return `Role: ${step.approverId}`;
      case 'manager':
        return 'Manager';
      case 'queue':
        return `Queue: ${step.approverId}`;
      case 'related_user':
        return `Related User (${step.approverField})`;
      default:
        return 'Unknown';
    }
  }

  /**
   * Save all processes to localStorage
   */
  private saveProcesses(processes: ApprovalProcess[]): void {
    localStorage.setItem(STORAGE_KEY_PROCESSES, JSON.stringify(processes));
  }

  /**
   * Save all requests to localStorage
   */
  private saveRequests(requests: ApprovalRequest[]): void {
    localStorage.setItem(STORAGE_KEY_REQUESTS, JSON.stringify(requests));
  }

  /**
   * Save a single request (update in array and persist)
   */
  private saveRequest(request: ApprovalRequest): ApprovalRequest {
    const requests = this.getAllRequests();
    const index = requests.findIndex(r => r.id === request.id);

    if (index >= 0) {
      requests[index] = request;
    } else {
      requests.push(request);
    }

    this.saveRequests(requests);
    return request;
  }

  /**
   * Initialize with mock data if empty
   */
  initializeWithMockData(processes: ApprovalProcess[], requests: ApprovalRequest[]): void {
    if (this.getAllProcesses().length === 0) {
      this.saveProcesses(processes);
    }
    if (this.getAllRequests().length === 0) {
      this.saveRequests(requests);
    }
  }

  /**
   * Clear all data (for testing)
   */
  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY_PROCESSES);
    localStorage.removeItem(STORAGE_KEY_REQUESTS);
  }
}

// Export singleton instance
export const approvalProcessStorage = new ApprovalProcessStorage();
